/**
 * needsReview — client-side "FR24 couldn't confirm this flight" tracking.
 *
 * Option A (no backend): when a non-terminal flight is well past departure and
 * FR24 gives no evidence it actually flew, we record it here so the UI can
 * surface a "needs review" prompt AND so the FR24-calling hooks stop re-querying
 * it. The system never makes the terminal call itself — a human resolves it.
 *
 * Persisted in localStorage (survives reloads + new sessions) — the point is to
 * avoid repeatedly hitting the costly FR24 endpoints for flights already known
 * to be unresolvable. Entries self-invalidate when the flight's status or
 * departure changes, and are cleared when a human resolves it (or re-checks).
 *
 * A `window` event lets components react to cache writes since localStorage
 * mutations don't trigger React re-renders on their own — see useNeedsReview.
 */
import { useEffect, useReducer } from 'react';

const KEY = 'gtj_needs_review';
export const NEEDS_REVIEW_HOURS = 24;
const CHANGE_EVENT = 'gtj:needs-review-changed';
const TERMINAL = new Set(['COMPLETED', 'DIVERTED', 'CANCELLED']);

// Tolerant accessors — work for both the raw DB flight (flight_id /
// flight_status / departure_date) and the track-list card shape (id / status /
// departureDate as a Date object).
const getId = (f) => f?.flight_id || f?.id || null;
const getStatus = (f) => (f?.flight_status || f?.status || '').toUpperCase();
const getDepartureMs = (f) => {
  const raw = f?.departure_date || f?.departureDate;
  if (!raw) return null;
  const ms = new Date(raw).getTime();
  return Number.isNaN(ms) ? null : ms;
};

const readMap = () => {
  try { return JSON.parse(localStorage.getItem(KEY) || '{}') || {}; }
  catch { return {}; }
};
const writeMap = (map) => {
  try { localStorage.setItem(KEY, JSON.stringify(map)); }
  catch { /* localStorage full / disabled — non-fatal */ }
};
const notify = () => {
  try { window.dispatchEvent(new Event(CHANGE_EVENT)); } catch { /* SSR / no window */ }
};

/** Non-terminal flight whose departure is more than NEEDS_REVIEW_HOURS ago. */
export function isPastReviewThreshold(flight) {
  const status = getStatus(flight);
  if (!status || TERMINAL.has(status)) return false;
  const depMs = getDepartureMs(flight);
  if (depMs == null) return false;
  return Date.now() > depMs + NEEDS_REVIEW_HOURS * 60 * 60 * 1000;
}

/** Record a flight as needs-review (FR24 exhausted). */
export function markNeedsReview(flight) {
  const id = getId(flight);
  if (!id) return;
  const map = readMap();
  map[id] = {
    markedAt: new Date().toISOString(),
    departureMs: getDepartureMs(flight),
    lastStatus: getStatus(flight),
  };
  writeMap(map);
  notify();
}

/** Forget a flight (after a human resolves it, on re-check, or when stale). */
export function clearNeedsReview(flightId) {
  if (!flightId) return;
  const map = readMap();
  if (map[flightId]) { delete map[flightId]; writeMap(map); notify(); }
}

/**
 * Display predicate: is this flight currently flagged needs-review? True when
 * it's cached AND still non-terminal. A terminal status (resolved by a human or
 * the automation) lazily evicts the entry.
 */
export function isNeedsReview(flight) {
  const id = getId(flight);
  if (!id) return false;
  if (!readMap()[id]) return false;
  if (TERMINAL.has(getStatus(flight))) { clearNeedsReview(id); return false; }
  return true;
}

/**
 * FR24-skip predicate for the calling hooks: skip the costly FR24 query when
 * the flight is already cached needs-review AND unchanged since. If the status
 * or departure moved, evict so it gets re-evaluated next time.
 */
export function shouldSkipFr24(flight) {
  const id = getId(flight);
  if (!id) return false;
  const entry = readMap()[id];
  if (!entry) return false;
  const stale =
    TERMINAL.has(getStatus(flight)) ||
    entry.lastStatus !== getStatus(flight) ||
    entry.departureMs !== getDepartureMs(flight);
  if (stale) { clearNeedsReview(id); return false; }
  return true;
}

/**
 * Reactive version of isNeedsReview for components — re-renders when the cache
 * changes (this tab via the custom event, or another tab via `storage`).
 */
export function useNeedsReview(flight) {
  const [, force] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    const handler = () => force();
    window.addEventListener(CHANGE_EVENT, handler);
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener(CHANGE_EVENT, handler);
      window.removeEventListener('storage', handler);
    };
  }, []);
  return isNeedsReview(flight);
}
